import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Check, ChevronLeft, Package, Tag, AlertCircle, LogOut, Search, Power, Info, Image, Upload, ShieldAlert } from 'lucide-react';
import { productApi, authApi, settingsApi } from '@/api-client';
import { VoucherManager } from '@/components/VoucherManager';
import { SEO } from '@/components/SEO';
import type { Product } from '@/types/product';
import { getPriceStr } from '@/types/product';

const CATEGORY_LABELS: Record<string, string> = {
  digital: 'Lifestyle-Spiele',
  software: 'Apps & Tools',
  gift: 'Besonderes',
};

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

export function Admin() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editProduct, setEditProduct] = useState<EditForm | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [shopEnabled, setShopEnabled] = useState(true);

  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  useEffect(() => {
    authApi.check()
      .then(data => { setAuthenticated(data.authenticated); setAuthLoading(false); })
      .catch(() => { setAuthenticated(false); setAuthLoading(false); });
  }, []);

  useEffect(() => {
    if (!authLoading && authenticated === false) navigate('/admin-login', { replace: true });
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated) {
      settingsApi.getShopStatus()
        .then(d => setShopEnabled(d.enabled))
        .catch(() => setShopEnabled(false));
    }
  }, [authenticated]);

  const loadProducts = useCallback(() => {
    setLoading(true);
    productApi.list().then(data => { setProducts(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (authenticated) loadProducts();
  }, [authenticated, loadProducts]);

  const handleLogout = async () => {
    try { await authApi.logout(); } finally {
      setAuthenticated(false); navigate('/admin-login', { replace: true });
    }
  };

  const toggleShop = async () => {
    try {
      const newState = !shopEnabled;
      await settingsApi.updateShopStatus(newState); // <-- KORRIGIERT: hieß setShopStatus
      setShopEnabled(newState);
      addToast(newState ? 'Shop ist jetzt LIVE' : 'Shop ist jetzt im Browsing-Modus', 'success');
    } catch { addToast('Fehler beim Umschalten', 'error'); }
  };

  const handleSave = async () => {
    if (!editProduct) return;
    setSaveLoading(true);
    try {
      const data = formToMutation(editProduct);
      if (editProduct.id) {
        await productApi.update({ id: editProduct.id, ...data });
        addToast('Produkt aktualisiert', 'success');
      } else {
        await productApi.create(data as any);
        addToast('Produkt erstellt', 'success');
      }
      loadProducts(); setIsEditing(false); setEditProduct(null);
    } catch { addToast('Fehler beim Speichern', 'error'); }
    setSaveLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await productApi.delete(deleteConfirm);
      loadProducts(); addToast('Produkt gelöscht', 'success'); setDeleteConfirm(null);
    } catch { addToast('Fehler beim Löschen', 'error'); }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    p.slug.toLowerCase().includes(productSearch.toLowerCase())
  );

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" /></div>;
  if (!authenticated) return null;

  return (
    <>
      <SEO title="Admin" description="Admin-Bereich – Produkt- und Gutscheinverwaltung." path="/admin" />
      <div className="mt-[70px] relative">
        {/* Toasts */}
        <div className="fixed top-24 right-6 z-[100] space-y-3">
          <AnimatePresence>
            {toasts.map(t => (
              <motion.div key={t.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }}
                className={`px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${t.type === 'success' ? 'bg-surface border-green-500/30 text-green-400' : 'bg-surface border-crimson/30 text-crimson'}`}>
                {t.message}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Header & Shop-Status */}
        <div className="border-b border-white/[0.06] bg-surface-secondary/50">
          <div className="max-w-[1200px] mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Admin-Bereich</h1>
                <p className="text-text-muted text-sm mt-1">Produkte, Gutscheine & Shop-Einstellungen verwalten</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={toggleShop}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all border shadow-sm ${shopEnabled ? 'bg-green-500/10 border-green-500/30 text-green-400 hover:bg-green-500/20' : 'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20'}`}>
                  <Power size={18} />
                  <span className={`w-2.5 h-2.5 rounded-full ${shopEnabled ? 'bg-green-400 animate-pulse' : 'bg-amber-400'}`} />
                  {shopEnabled ? 'Shop AN' : 'Shop AUS (Browsing)'}
                </button>
                <button onClick={() => { setEditProduct(emptyForm()); setIsEditing(true); }}
                  className="flex items-center gap-2 px-5 py-3 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all">
                  <Plus size={18} />Produkt hinzufügen
                </button>
                <button onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-3 bg-surface border border-white/[0.06] text-text-secondary rounded-xl hover:border-crimson/30 hover:text-crimson transition-all" title="Abmelden">
                  <LogOut size={16} /><span className="hidden sm:inline">Abmelden</span>
                </button>
              </div>
            </div>

            <div className={`flex items-start gap-3 p-4 rounded-xl border ${shopEnabled ? 'bg-green-500/5 border-green-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
              <Info size={20} className={`mt-0.5 flex-shrink-0 ${shopEnabled ? 'text-green-400' : 'text-amber-400'}`} />
              <div>
                <p className={`text-sm font-semibold ${shopEnabled ? 'text-green-400' : 'text-amber-400'}`}>
                  {shopEnabled
                    ? 'Shop ist LIVE – Kunden können Produkte ansehen und kaufen.'
                    : 'Shop ist im Browsing-Modus – Kunden können Produkte ansehen, aber NICHT kaufen.'}
                </p>
                <p className="text-text-muted text-xs mt-1 leading-relaxed">
                  {shopEnabled
                    ? 'Alle Funktionen aktiv. Kauf-Button, Warenkorb und Checkout sind sichtbar.'
                    : 'Die Produktdetailseiten bleiben öffentlich erreichbar. Der Kauf-Button und der Warenkorb werden ausgeblendet. Der Shop bleibt „offen" zum Durchstöbern.'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PRODUKTE */}
        <section className="max-w-[1200px] mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
            <div>
              <h2 className="text-xl font-bold text-text-primary mb-1">Produktverwaltung</h2>
              <p className="text-text-muted text-sm">{products.length} Produkte im Shop</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input type="text" placeholder="Produkte suchen..." value={productSearch} onChange={e => setProductSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-surface border border-white/[0.06] rounded-xl text-sm text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all w-64" />
              </div>
              <div className="flex items-center gap-2 text-text-muted text-sm"><span className="w-2 h-2 bg-green-400 rounded-full" />Geschützt</div>
            </div>
          </div>

          <div className="bg-surface rounded-2xl border border-white/[0.06] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06] bg-surface-secondary/50">
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Produkt</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Kategorie</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Preis</th>
                    <th className="text-left text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Status</th>
                    <th className="text-right text-xs font-bold uppercase tracking-wider text-text-muted px-5 py-3">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => (
                    <tr key={product.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" onError={e => { (e.target as HTMLImageElement).src = '/images/placeholder.jpg'; }} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="font-semibold text-text-primary text-sm truncate">{product.name}</div>
                              {(product as any).isAdult && (
                                <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-crimson-dim text-crimson text-[10px] font-bold">18+</span>
                              )}
                            </div>
                            <div className="text-text-muted text-xs">{product.slug}</div>
                          </div>
                          {product.badge && (
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${product.badgeColor === 'crimson' ? 'bg-crimson-dim text-crimson' : 'bg-gold-dim text-gold'}`}>{product.badge}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.04] text-text-secondary text-xs"><Tag size={12} />{CATEGORY_LABELS[product.category] || product.category}</span>
                      </td>
                      <td className="px-5 py-4">
                        <PriceCell product={product} />
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${product.inStock ? 'bg-green-500/10 text-green-400' : 'bg-text-muted/10 text-text-muted'}`}>{product.inStock ? 'Auf Lager' : 'Nicht verfügbar'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => { setEditProduct(formFromProduct(product)); setIsEditing(true); }} className="p-2 rounded-lg bg-white/[0.04] text-text-muted hover:text-gold hover:bg-gold/10 transition-all" title="Bearbeiten"><Pencil size={16} /></button>
                          <button onClick={() => setDeleteConfirm(product.id)} className="p-2 rounded-lg bg-white/[0.04] text-text-muted hover:text-crimson hover:bg-crimson/10 transition-all" title="Löschen"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredProducts.length === 0 && !loading && (
              <div className="text-center py-16">
                <Package size={48} className="text-text-muted mx-auto mb-4" />
                <p className="text-text-secondary font-medium mb-1">{productSearch ? 'Keine Produkte gefunden' : 'Keine Produkte vorhanden'}</p>
                <p className="text-text-muted text-sm mb-4">{productSearch ? 'Passe deine Suche an.' : 'Füge dein erstes Produkt hinzu.'}</p>
                {!productSearch && <button onClick={() => { setEditProduct(emptyForm()); setIsEditing(true); }} className="px-5 py-2.5 bg-gold text-surface-primary font-semibold rounded-xl">Produkt hinzufügen</button>}
              </div>
            )}
          </div>
        </section>

        {/* GUTSCHEINE */}
        <section className="max-w-[1200px] mx-auto px-6 py-8 border-t border-white/[0.06]">
          <VoucherManager />
        </section>

        <AnimatePresence>
          {isEditing && editProduct && (
            <ProductForm form={editProduct} onChange={setEditProduct} onSave={handleSave} onCancel={() => { setIsEditing(false); setEditProduct(null); }} isLoading={saveLoading} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {deleteConfirm !== null && (
            <DeleteConfirm title="Produkt löschen?" message="Dieses Produkt wird unwiderruflich entfernt." onConfirm={handleDelete} onCancel={() => setDeleteConfirm(null)} />
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

/* ---------- Types ---------- */
interface EditForm {
  id?: number; slug: string; name: string; description: string; longDescription: string;
  price: string; originalPrice: string; image: string; category: string;
  badge: string; badgeColor: string; features: string; inStock: boolean; rating: string; reviews: string;
  downloadFile: string;
  screenshots: string[];
  priceType: 'fixed' | 'free' | 'inquiry';
  isAdult: boolean;
}

function emptyForm(): EditForm {
  return { slug: '', name: '', description: '', longDescription: '', price: '', originalPrice: '', image: '',
    category: 'digital', badge: '', badgeColor: 'gold', features: '', inStock: true, rating: '', reviews: '',
    downloadFile: '', screenshots: [], priceType: 'fixed', isAdult: false };
}

function formFromProduct(p: Product): EditForm {
  return {
    id: p.id, slug: p.slug, name: p.name, description: p.description, longDescription: p.longDescription ?? '',
    price: p.price ?? '', originalPrice: p.originalPrice ?? '', image: p.image ?? '', category: p.category ?? 'digital',
    badge: p.badge ?? '', badgeColor: p.badgeColor ?? 'gold',
    features: Array.isArray(p.features) ? p.features.join(', ') : '',
    inStock: p.inStock ?? true, rating: p.rating ?? '', reviews: p.reviews ?? '',
    downloadFile: (p as any).downloadFile ?? '',
    screenshots: Array.isArray((p as any).screenshots) ? (p as any).screenshots : [],
    priceType: (p as any).priceType || 'fixed',
    isAdult: (p as any).isAdult ?? false,
  };
}

function formToMutation(form: EditForm): Record<string, unknown> {
  const result: Record<string, unknown> = {
    slug: form.slug, name: form.name, description: form.description,
    price: form.price, category: form.category, inStock: form.inStock,
    image: form.image || '/images/placeholder.jpg',
    priceType: form.priceType,
    isAdult: form.isAdult ? 1 : 0,
  };
  if (form.longDescription) result.longDescription = form.longDescription;
  if (form.originalPrice) result.originalPrice = form.originalPrice;
  if (form.badge) result.badge = form.badge;
  if (form.badgeColor) result.badgeColor = form.badgeColor;
  if (form.features) result.features = form.features.split(',').map(f => f.trim()).filter(Boolean);
  if (form.rating) result.rating = form.rating;
  if (form.reviews) result.reviews = form.reviews;
  if (form.downloadFile) result.downloadFile = form.downloadFile;
  if (form.screenshots.length > 0) result.screenshots = form.screenshots;
  return result;
}

/* ---------- Price Cell ---------- */
function PriceCell({ product }: { product: Product }) {
  const type = (product as any).priceType || 'fixed';
  if (type === 'free') return <span className="text-green-400 font-bold text-sm">Kostenlos</span>;
  if (type === 'inquiry') return <span className="text-text-secondary font-bold text-sm">Auf Anfrage</span>;
  return (
    <>
      <span className="text-gold font-bold text-sm">{getPriceStr(product.price)} €</span>
      {product.originalPrice && <span className="text-text-muted text-xs line-through ml-2">{getPriceStr(product.originalPrice)} €</span>}
    </>
  );
}

/* ---------- Upload Helper ---------- */
const UPLOAD_SECRET = 'JLSoft2026!Upload';

async function uploadFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_key', UPLOAD_SECRET);

  const res = await fetch('/api/upload.php', {
    method: 'POST',
    body: formData,
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(err || 'Upload fehlgeschlagen');
  }

  const data = await res.json();
  if (!data.success) throw new Error(data.error || 'Upload fehlgeschlagen');
  return data.url;
}

/* ---------- Form Modal ---------- */
function ProductForm({ form, onChange, onSave, onCancel, isLoading }: {
  form: EditForm; onChange: (f: EditForm) => void; onSave: () => void; onCancel: () => void; isLoading: boolean;
}) {
  const update = (patch: Partial<EditForm>) => onChange({ ...form, ...patch });
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const screenshotInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingScreenshots, setUploadingScreenshots] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Bitte wähle eine Bilddatei'); return; }
    if (file.size > 5 * 1024 * 1024) { alert('Maximale Größe: 5MB'); return; }

    setUploadingImage(true);
    try {
      const url = await uploadFile(file);
      update({ image: url });
    } catch (err: any) {
      alert('Fehler beim Upload: ' + err.message);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(f => {
      if (!f.type.startsWith('image/')) { alert(`${f.name} ist kein Bild`); return false; }
      if (f.size > 5 * 1024 * 1024) { alert(`${f.name} ist zu groß (max. 5MB)`); return false; }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploadingScreenshots(true);
    try {
      const urls = await Promise.all(validFiles.map(uploadFile));
      update({ screenshots: [...form.screenshots, ...urls] });
    } catch (err: any) {
      alert('Fehler beim Upload: ' + err.message);
    } finally {
      setUploadingScreenshots(false);
      if (screenshotInputRef.current) screenshotInputRef.current.value = '';
    }
  };

  const removeScreenshot = (index: number) => {
    const newScreenshots = form.screenshots.filter((_, i) => i !== index);
    update({ screenshots: newScreenshots });
  };

  const handlePriceTypeChange = (type: 'fixed' | 'free' | 'inquiry') => {
    if (type === 'free') {
      update({ priceType: type, price: '0' });
    } else if (type === 'inquiry') {
      update({ priceType: type, price: '' });
    } else {
      update({ priceType: type });
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-10 pb-10 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        className="relative w-full max-w-2xl bg-surface-primary border border-white/[0.06] rounded-2xl shadow-2xl mx-4">
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <h2 className="text-lg font-bold text-text-primary">{form.id ? 'Produkt bearbeiten' : 'Neues Produkt'}</h2>
          <button onClick={onCancel} className="p-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-white/[0.05] transition-all"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Slug (URL) *</label>
              <input value={form.slug} onChange={e => update({ slug: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="z.B. lustduell" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Name *</label>
              <input value={form.name} onChange={e => update({ name: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="Produktname" />
            </div>

            {/* PREISTYP */}
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Preistyp *</label>
              <select 
                value={form.priceType} 
                onChange={e => handlePriceTypeChange(e.target.value as 'fixed' | 'free' | 'inquiry')}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
              >
                <option value="fixed">Fester Preis</option>
                <option value="free">Kostenlos</option>
                <option value="inquiry">Preis auf Anfrage</option>
              </select>
            </div>

            {/* PREIS – nur bei Festpreis */}
            {form.priceType === 'fixed' && (
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">Preis (€) *</label>
                <input type="number" step="0.01" value={form.price} onChange={e => update({ price: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="9.99" />
              </div>
            )}
            {form.priceType !== 'fixed' && <div />}

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Originalpreis (€) <span className="text-text-muted">optional</span></label>
              <input type="number" step="0.01" value={form.originalPrice} onChange={e => update({ originalPrice: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="14.99" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Kategorie *</label>
              <select value={form.category} onChange={e => update({ category: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all">
                <option value="digital">Lifestyle-Spiele</option>
                <option value="software">Apps & Tools</option>
                <option value="gift">Besonderes</option>
              </select>
            </div>

            {/* HAUPTBILD */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Produktbild (Logo/Cover)</label>
              {form.image ? (
                <div className="flex items-start gap-3">
                  <img src={form.image} alt="Vorschau" className="w-24 h-24 object-cover rounded-lg border border-white/[0.06]" />
                  <div className="flex-1 space-y-2">
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-gold-dim text-gold border border-gold/20 rounded-xl text-sm font-medium hover:bg-gold/25 transition-all">Bild ändern</button>
                    <button type="button" onClick={() => update({ image: '' })}
                      className="block px-4 py-2 text-text-muted text-sm hover:text-crimson transition-colors">Bild entfernen</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="w-full h-32 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/[0.06] rounded-xl hover:border-gold/25 hover:bg-gold/[0.02] transition-all">
                  {uploadingImage ? (
                    <div className="animate-spin w-6 h-6 border-2 border-gold border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Upload size={24} className="text-text-muted" />
                      <span className="text-sm text-text-muted">Cover-Bild hochladen (JPG, PNG, WebP)</span>
                      <span className="text-xs text-text-muted">Max. 5MB</span>
                    </>
                  )}
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </div>

            {/* SCREENSHOTS */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Screenshots <span className="text-text-muted">optional</span>
              </label>
              {form.screenshots.length > 0 && (
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {form.screenshots.map((src, idx) => (
                    <div key={idx} className="relative group aspect-square">
                      <img src={src} alt={`Screenshot ${idx + 1}`} className="w-full h-full object-cover rounded-lg border border-white/[0.06]" />
                      <button
                        onClick={() => removeScreenshot(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-crimson text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        title="Entfernen"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => screenshotInputRef.current?.click()}
                className="w-full h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-white/[0.06] rounded-xl hover:border-gold/25 hover:bg-gold/[0.02] transition-all">
                {uploadingScreenshots ? (
                  <>
                    <div className="animate-spin w-5 h-5 border-2 border-gold border-t-transparent rounded-full" />
                    <span className="text-sm text-text-muted">Wird hochgeladen...</span>
                  </>
                ) : (
                  <>
                    <Image size={20} className="text-text-muted" />
                    <span className="text-sm text-text-muted">Screenshots hochladen (mehrere möglich)</span>
                    <span className="text-xs text-text-muted">Max. 5MB pro Bild</span>
                  </>
                )}
              </button>
              <input ref={screenshotInputRef} type="file" accept="image/*" multiple onChange={handleScreenshotUpload} className="hidden" />
            </div>

            {/* DOWNLOAD-FELD */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Download-Datei <span className="text-text-muted">optional</span>
              </label>
              <input
                value={form.downloadFile}
                onChange={e => update({ downloadFile: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all font-mono"
                placeholder="z.B. LustDuell-v1.2.apk"
              />
              <p className="text-xs text-text-muted mt-1">Datei muss per FTP in /downloads/ liegen</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Badge <span className="text-text-muted">optional</span></label>
              <input value={form.badge} onChange={e => update({ badge: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="z.B. Neu, Bestseller" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Badge-Farbe</label>
              <select value={form.badgeColor} onChange={e => update({ badgeColor: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all">
                <option value="gold">Gold</option><option value="crimson">Crimson</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Bewertung <span className="text-text-muted">optional</span></label>
              <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => update({ rating: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="4.8" />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">Anzahl Bewertungen <span className="text-text-muted">optional</span></label>
              <input type="number" value={form.reviews} onChange={e => update({ reviews: e.target.value })}
                className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="42" />
            </div>
          </div>
          
          {/* 18+ CHECKBOX */}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-crimson/5 border border-crimson/10">
            <input type="checkbox" id="isAdult" checked={form.isAdult} onChange={e => update({ isAdult: e.target.checked })} className="w-5 h-5 accent-crimson rounded" />
            <label htmlFor="isAdult" className="text-sm text-text-secondary select-none cursor-pointer">
              <span className="font-semibold text-crimson">Inhalt nur für Erwachsene</span> (18+ / FSK 18)
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Features <span className="text-text-muted">kommagetrennt</span></label>
            <input value={form.features} onChange={e => update({ features: e.target.value })}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all" placeholder="2-4 Spieler, 45-90 Min., Android" />
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="inStock" checked={form.inStock} onChange={e => update({ inStock: e.target.checked })} className="w-5 h-5 accent-gold rounded" />
            <label htmlFor="inStock" className="text-sm text-text-secondary select-none cursor-pointer">Auf Lager</label>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Kurzbeschreibung *</label>
            <textarea value={form.description} onChange={e => update({ description: e.target.value })} rows={2}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all resize-y" placeholder="Kurze Beschreibung für die Übersicht..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1.5">Lange Beschreibung <span className="text-text-muted">optional – Absätze mit Enter</span></label>
            <textarea value={form.longDescription} onChange={e => update({ longDescription: e.target.value })} rows={6}
              className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all resize-y whitespace-pre-wrap" placeholder="Detaillierte Beschreibung für die Produktdetailseite..." />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-white/[0.06]">
          <button onClick={onCancel} className="px-5 py-2.5 text-text-secondary font-medium rounded-xl hover:bg-white/[0.04] transition-all">Abbrechen</button>
          <button onClick={onSave} 
            disabled={isLoading || !form.slug || !form.name || !form.description || (form.priceType === 'fixed' && !form.price)}
            className="flex items-center gap-2 px-6 py-2.5 bg-gold text-surface-primary font-semibold rounded-xl hover:shadow-gold transition-all disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? <div className="w-4 h-4 border-2 border-surface-primary border-t-transparent rounded-full animate-spin" /> : <Check size={18} />}
            Speichern
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* ---------- Delete Confirmation ---------- */
function DeleteConfirm({ title, message, onConfirm, onCancel }: { title: string; message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-surface border border-white/[0.06] rounded-2xl shadow-2xl p-6 max-w-sm mx-4">
        <div className="flex items-start gap-3 mb-4">
          <AlertCircle size={24} className="text-crimson flex-shrink-0" />
          <div>
            <h3 className="font-bold text-text-primary mb-1">{title}</h3>
            <p className="text-text-secondary text-sm">{message}</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-text-secondary text-sm font-medium rounded-lg hover:bg-white/[0.04] transition-all">Abbrechen</button>
          <button onClick={onConfirm} className="px-4 py-2 bg-crimson text-white text-sm font-medium rounded-lg hover:bg-crimson/80 transition-all">Löschen</button>
        </div>
      </motion.div>
    </div>
  );
}